import { type UseMutationResult, useMutation } from "@tanstack/react-query"
import { AxiosError, type AxiosResponse, HttpStatusCode } from "axios"
import eq from "lodash-es/eq"

import { minutes, seconds } from "@/3514/utils"

import { ECoachingChatMutationKey as EMutationKey } from "../../config"
import {
    type ICoachingChatConversationSummaryCardQueryDto as ISummaryCardDto,
    type TCoachingChatConversationStatusDto as TConversationStatus
} from "../../types"

import { coachingChatMutationsMutators as mutators } from "./coaching-chat.api.mutations.mutators"

// socket token --start
type TSocketTokenResponse = AxiosResponse<{ token: string }>

type TSocketTokenOptions = {
    onSuccess(response: TSocketTokenResponse, vars: void, context: void): Promise<void>
    onError(error: AxiosError, vars: void, context: void): Promise<void>
}

type TSocketTokenMutation = UseMutationResult<TSocketTokenResponse, AxiosError, void, void>

function useSocketTokenMutation(
    options: TSocketTokenOptions
): ReturnType<(options: TSocketTokenOptions) => TSocketTokenMutation> {
    return useMutation<TSocketTokenResponse, AxiosError, void, void>({
        mutationKey: EMutationKey.CreateSocketToken(),
        mutationFn: mutators.socketToken,
        gcTime: minutes(1),
        retry: 1,
        retryDelay: seconds(2),
        networkMode: "offlineFirst",
        throwOnError: false,
        ...options
    })
}
// socket token --end

// seed answer --start
type TSeedAnswerParams = {
    question_uuid: string
    answer: string
}

type TSeedAnswerContext = { questionUuid: string; answer: string }

type TSeedAnswerResponse = AxiosResponse<{ all_answered: boolean }>

type TSeedAnswerError = AxiosError<{ error: { msg: string; moderation_id: number } }>

type TSeedAnswerMutateOnMutateFunction = (response: TSeedAnswerParams) => Promise<TSeedAnswerContext>
type TSeedAnswerMutateOnSuccessFunction = (
    response: TSeedAnswerResponse,
    vars: TSeedAnswerParams,
    context: TSeedAnswerContext
) => Promise<void>
type TSeedAnswerMutateOnErrorFunction = (
    error: AxiosError,
    vars: TSeedAnswerParams,
    context: TSeedAnswerContext
) => Promise<void>

type TSeedAnswerOptions = {
    chapterId: number
    chapterComponentId: number
    onMutate: TSeedAnswerMutateOnMutateFunction
    onSuccess: TSeedAnswerMutateOnSuccessFunction
    onError: TSeedAnswerMutateOnErrorFunction
}

type TSeedAnswerMutation = UseMutationResult<TSeedAnswerResponse, AxiosError, TSeedAnswerParams, TSeedAnswerContext>

function useSeedAnswerMutation({
    chapterId,
    chapterComponentId,
    ...rest
}: TSeedAnswerOptions): ReturnType<(options: TSeedAnswerOptions) => TSeedAnswerMutation> {
    return useMutation<TSeedAnswerResponse, TSeedAnswerError, TSeedAnswerParams, TSeedAnswerContext>({
        mutationKey: EMutationKey.Seed(chapterId),
        mutationFn: ({ question_uuid, answer }: TSeedAnswerParams): Promise<TSeedAnswerResponse> =>
            mutators.seedAnswer({
                question_uuid,
                answer,
                chapterId,
                chapterComponentId
            }),
        gcTime: 0,
        retry: (failureCount: number, error: AxiosError): boolean =>
            eq(error.status, HttpStatusCode.UnprocessableEntity) ? false : failureCount <= 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        throwOnError: false,
        ...rest
    })
}
// seed answer --end

// conversation --start
type _TConversationParams = {}

type TConversationContext = {}

type TConversationResponse = AxiosResponse<{
    id: number
    status: TConversationStatus
    completed_stamp: null | string | undefined
    is_matrix_feedback_completed: boolean
    summary_cards: ISummaryCardDto[]
} | null>

type TConversationOptions = {
    chapterId: number
    chapterComponentId: number
    onSuccess?: (
        response: TConversationResponse,
        vars: _TConversationParams,
        context: TConversationContext
    ) => Promise<void>
}

type TConversationMutation = UseMutationResult<TConversationResponse, AxiosError, _TConversationParams>

function useConversationMutation({
    chapterId,
    chapterComponentId,
    ...rest
}: TConversationOptions): ReturnType<(options: TConversationOptions) => TConversationMutation> {
    return useMutation<TConversationResponse, AxiosError, _TConversationParams, TConversationContext>({
        mutationKey: EMutationKey.CreateConversation(chapterId),
        mutationFn: (): Promise<TConversationResponse> => mutators.conversation({ chapterId, chapterComponentId }),
        gcTime: 0,
        retry: (failureCount: number, error: AxiosError): boolean =>
            eq(error.status, HttpStatusCode.InternalServerError) ? failureCount <= 1 : false,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        throwOnError: false,
        ...rest
    })
}
// conversation --end

// message --start
type TMessageParams = {
    conversationId: number
    message: string
}

type TMessageContext = { sentMessageId: string; placeholderMessageId: string }

type TMessageResponse = AxiosResponse<{ message_id: number }>

type _TMessageOptions = {
    onMutate(response: TMessageParams): Promise<TMessageContext>
    onSuccess(response: TMessageResponse, vars: TMessageParams, context: TMessageContext): Promise<void>
    onError(error: AxiosError, vars: TMessageParams, context: TMessageContext): Promise<void>
}

type TMessageMutation = UseMutationResult<TMessageResponse, AxiosError, TMessageParams, TMessageContext>

function useMessageMutation(options: _TMessageOptions): ReturnType<(options: _TMessageOptions) => TMessageMutation> {
    return useMutation<TMessageResponse, AxiosError, TMessageParams, TMessageContext>({
        mutationKey: EMutationKey.CreateMessage(),
        mutationFn: mutators.message,
        gcTime: minutes(1),
        retry: (failureCount: number, error: AxiosError): boolean =>
            eq(error.status, HttpStatusCode.InternalServerError) ? failureCount <= 1 : false,
        retryDelay: seconds(2),
        networkMode: "offlineFirst",
        throwOnError: false,
        ...options
    })
}
// message --end

// legalise record --start
type TDisclaimerAwarenessOptions = {
    onMutate(vars: void): Promise<void>
    onSuccess(response: AxiosResponse, vars: void, context: void): Promise<void>
    onError(error: AxiosError, vars: void, context: void): Promise<void>
}

type TDisclaimerAwarenessMutation = UseMutationResult<AxiosResponse, AxiosError, void, void>

function useDisclaimerAwarenessMutation(
    options: TDisclaimerAwarenessOptions
): ReturnType<(options: TDisclaimerAwarenessOptions) => TDisclaimerAwarenessMutation> {
    return useMutation<AxiosResponse, AxiosError, void, void>({
        mutationKey: EMutationKey.CreateDisclaimerAwareness(),
        mutationFn: mutators.disclaimer,
        gcTime: 0,
        retry: (failureCount: number, error: AxiosError): boolean =>
            eq(error.status, HttpStatusCode.InternalServerError) ? failureCount <= 1 : false,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        throwOnError: false,
        ...options
    })
}
// legalise record --end

// matrix feedback --start
type TMatrixFeedbackParams = {
    conversationId: number
    text: string
    data: object
}

type TMatrixFeedbackOptions = {
    onMutate(vars: TMatrixFeedbackParams): Promise<void>
    onSuccess(response: AxiosResponse, vars: TMatrixFeedbackParams, context: void): Promise<void>
    onError(error: AxiosError, vars: TMatrixFeedbackParams, context: void): Promise<void>
}

type TMatrixFeedbackMutation = UseMutationResult<AxiosResponse, AxiosError, TMatrixFeedbackParams, void>

function useMatrixFeedbackMutation(
    options: TMatrixFeedbackOptions
): ReturnType<(options: TMatrixFeedbackOptions) => TMatrixFeedbackMutation> {
    return useMutation<AxiosResponse, AxiosError, TMatrixFeedbackParams, void>({
        mutationKey: EMutationKey.CreateMatrixFeedback(),
        mutationFn: mutators.matrixFeedback,
        gcTime: 0,
        retry: (failureCount: number, error: AxiosError): boolean =>
            eq(error.status, HttpStatusCode.InternalServerError) ? failureCount <= 1 : false,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        throwOnError: false,
        ...options
    })
}
// matrix feedback --end

export {
    useSocketTokenMutation as useCoachingChatSocketTokenMutation,
    useSeedAnswerMutation as useCoachingChatSeedMutation,
    useConversationMutation as useCoachingChatCreateConversationMutation,
    useMessageMutation as useCoachingChatCreateMessageMutation,
    useDisclaimerAwarenessMutation as useCoachingChatCreateDisclaimerAwarenessMutation,
    useMatrixFeedbackMutation as useCoachingChatCreateMatrixFeedbackMutation
}
export type {
    TSeedAnswerParams as TCoachingChatSeedMutationParams,
    TSeedAnswerContext as TCoachingChatSeedMutationContext,
    TSeedAnswerResponse as TCoachingChatSeedMutationResponse,
    TSeedAnswerError as TCoachingChatSeedMutationError,
    TSeedAnswerOptions as TCoachingChatSeedMutationOptions,
    TSeedAnswerMutateOnMutateFunction as TCoachingChatSeedMutationOnMutateFunction,
    TSeedAnswerMutateOnSuccessFunction as TCoachingChatSeedMutationOnSuccessFunction,
    TSeedAnswerMutateOnErrorFunction as TCoachingChatSeedMutationOnErrorFunction,
    TSeedAnswerMutation as TCoachingChatSeedMutation,
    TConversationResponse as TCoachingChatCreateConversationMutationResponse,
    TConversationOptions as TCoachingChatCreateConversationMutationOptions,
    TConversationMutation as TCoachingChatCreateConversationMutation,
    TMessageParams as TCoachingChatCreateMessageMutationParams,
    TMessageContext as TCoachingChatCreateMessageMutationContext,
    TMessageResponse as TCoachingChatCreateMessageMutationResponse,
    TMessageMutation as TCoachingChatCreateMessageMutation,
    TSocketTokenMutation as TCoachingChatCreateSocketTokenMutation,
    TSocketTokenResponse as TCoachingChatCreateSocketTokenMutationResponse,
    TDisclaimerAwarenessOptions as TCoachingChatCreateDisclaimerAwarenessMutationOptions,
    TDisclaimerAwarenessMutation as TCoachingChatCreateDisclaimerAwarenessMutation,
    TMatrixFeedbackOptions as TCoachingChatCreateMatrixFeedbackMutationOptions,
    TMatrixFeedbackParams as TCoachingChatCreateMatrixFeedbackMutationParams,
    TMatrixFeedbackMutation as TCoachingChatCreateMatrixFeedbackMutation
}
